
import { Node, NodeType } from '../types';

const pathCache = new Map<string, string[]>();
let lastLayoutVersionForCache = -1;

export const getUnreachableNodes = (nodes: Node[]): string[] => {
  const entranceNodes = nodes.filter(n => 
    n.type === NodeType.ENTRANCE || n.type === NodeType.AMBULANCE_ENTRANCE
  );
  if (entranceNodes.length === 0) return nodes.length > 0 ? nodes.slice(1).map(n => n.id) : [];

  const visited = new Set<string>();
  const queue: string[] = entranceNodes.map(n => n.id);
  entranceNodes.forEach(n => visited.add(n.id));
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const node = nodeMap.get(currentId);
    if (node) {
      for (const neighborId of node.connections) {
        if (!visited.has(neighborId)) {
          visited.add(neighborId);
          queue.push(neighborId);
        }
      }
    }
  }
  return nodes.filter(n => !visited.has(n.id)).map(n => n.id);
};

/**
 * Optimizuotas Dijkstra algoritmas
 */
export const findPath = (
  startNodeId: string,
  endNodeId: string,
  nodes: Node[],
  avoidContamination: boolean = false,
  congestionAwareness: number = 0,
  layoutVersion: number = 0
): string[] | null => {
  if (layoutVersion !== lastLayoutVersionForCache) {
    pathCache.clear();
    lastLayoutVersionForCache = layoutVersion;
  }

  const cacheKey = `${startNodeId}-${endNodeId}-${avoidContamination}-${congestionAwareness}`;
  const canUseCache = !avoidContamination && congestionAwareness === 0;
  if (canUseCache && pathCache.has(cacheKey)) return [...pathCache.get(cacheKey)!];

  if (startNodeId === endNodeId) return [startNodeId];

  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const minDistance = new Map<string, number>();
  const previousNode = new Map<string, string | null>();
  
  // Naudojame Set greitam tikrinimui ar mazgas eilėje
  const openSet = new Set<string>([startNodeId]);
  minDistance.set(startNodeId, 0);

  while (openSet.size > 0) {
    // VIETOJ .sort() surandame mažiausią atstumą (O(N) vietoj O(N log N))
    let currentId: string | null = null;
    let shortestDist = Infinity;
    
    for (const id of openSet) {
      const dist = minDistance.get(id) ?? Infinity;
      if (dist < shortestDist) {
        shortestDist = dist;
        currentId = id;
      }
    }

    if (currentId === null || currentId === endNodeId) break;
    openSet.delete(currentId);

    const currentNode = nodeMap.get(currentId);
    if (!currentNode) continue;

    for (const neighborId of currentNode.connections) {
      const neighbor = nodeMap.get(neighborId);
      if (!neighbor) continue;

      let edgeWeight = 1;
      if (avoidContamination) edgeWeight += (neighbor.contamination / 15);
      if (congestionAwareness > 0) {
        const occupancy = neighbor.capacity > 0 ? neighbor.occupants.length / neighbor.capacity : 0;
        edgeWeight += (occupancy * 10 * congestionAwareness);
      }

      const dx = currentNode.x - neighbor.x;
      const dy = currentNode.y - neighbor.y;
      const weight = edgeWeight * Math.sqrt(dx * dx + dy * dy);
      const totalDistance = (minDistance.get(currentId) || 0) + weight;

      if (totalDistance < (minDistance.get(neighborId) ?? Infinity)) {
        minDistance.set(neighborId, totalDistance);
        previousNode.set(neighborId, currentId);
        openSet.add(neighborId);
      }
    }
  }

  if (!previousNode.has(endNodeId)) return null;

  const fullPath: string[] = [];
  let step: string | null = endNodeId;
  while (step) {
    fullPath.unshift(step);
    step = previousNode.get(step) || null;
  }
  
  if (canUseCache) pathCache.set(cacheKey, fullPath);
  return [...fullPath];
};

export const findNearestNodeByType = (
  startNodeId: string,
  targetType: string,
  nodes: Node[],
  checkCapacity: boolean = false
): string | null => {
  if (!nodes.some(node => node.type === targetType)) return null;

  const queue: string[] = [startNodeId];
  const visited = new Set<string>([startNodeId]);
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  while (queue.length > 0) {
    const currentId = queue.shift()!;
    const node = nodeMap.get(currentId);
    
    if (node && node.type === targetType && node.id !== startNodeId) {
       if (!checkCapacity || node.occupants.length < node.capacity) return node.id;
    }

    if (node) {
      for (const neighborId of node.connections) {
        if (!visited.has(neighborId)) {
          visited.add(neighborId);
          queue.push(neighborId);
        }
      }
    }
  }
  return null;
};
